import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Check, Loader2, Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'
import {
  useSROConfig,
  useSROIntents,
  useSRODepartments,
  useSROServiceArea,
  useUpdateSROConfig,
  useCreateSROConfig,
  useCreateSROIntent,
  useUpdateSROIntent,
  useDeleteSROIntent,
  useCreateSRODepartment,
  useUpdateSRODepartment,
  useDeleteSRODepartment,
  useCreateSROSubDepartment,
  useUpdateSROSubDepartment,
  useDeleteSROSubDepartment,
  useUpdateSROServiceArea,
} from '@/hooks/useSRO'
import { StepBasics } from './wizard-steps/StepBasics'
import { StepDepartments } from './wizard-steps/StepDepartments'
import { StepIntents } from './wizard-steps/StepIntents'
import { StepIntegration } from './wizard-steps/StepIntegration'
import { StepServiceArea } from './wizard-steps/StepServiceArea'
import { StepReview } from './wizard-steps/StepReview'
import {
  WIZARD_STEPS,
  INITIAL_WIZARD_DATA,
  type WizardData,
  type WizardDepartment,
  type WizardIntent,
} from './wizard-steps/types'

const STEP_KEY_TO_ID: Record<string, number> = {}
WIZARD_STEPS.forEach((s) => {
  STEP_KEY_TO_ID[s.key] = s.id
})

export function SROWizard() {
  const { configId } = useParams<{ configId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  const isEdit = !!configId
  const numConfigId = configId ? parseInt(configId) : undefined

  // Fetch existing data for edit mode
  const { data: existingConfig, isLoading: configLoading } = useSROConfig(numConfigId)
  const { data: existingIntents } = useSROIntents(numConfigId)
  const { data: existingDepts } = useSRODepartments(numConfigId)
  const { data: existingSA } = useSROServiceArea(numConfigId)

  // Mutations
  const createConfig = useCreateSROConfig()
  const updateConfig = useUpdateSROConfig()
  const createIntent = useCreateSROIntent()
  const updateIntent = useUpdateSROIntent()
  const deleteIntent = useDeleteSROIntent()
  const createDept = useCreateSRODepartment()
  const updateDept = useUpdateSRODepartment()
  const deleteDept = useDeleteSRODepartment()
  const createSub = useCreateSROSubDepartment()
  const updateSub = useUpdateSROSubDepartment()
  const deleteSub = useDeleteSROSubDepartment()
  const updateSA = useUpdateSROServiceArea()

  // Wizard state
  const initialStep = (() => {
    const stepParam = searchParams.get('step')
    if (stepParam && STEP_KEY_TO_ID[stepParam]) return STEP_KEY_TO_ID[stepParam]
    return 1
  })()

  const [currentStep, setCurrentStep] = useState(initialStep)
  const [wizardData, setWizardData] = useState<WizardData>(INITIAL_WIZARD_DATA)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [loaded, setLoaded] = useState(!isEdit)

  // Populate wizard data from existing config
  useEffect(() => {
    if (!isEdit || !existingConfig) return

    const populated: WizardData = {
      configName: existingConfig.name || '',
      provider: existingConfig.llm_provider || 'openai',
      model: existingConfig.llm_model || 'gpt-4o-mini',
      sessionTTL: existingConfig.session_ttl || 1800,
      enableSRO: existingConfig.is_enabled ?? true,
      departments: (existingDepts || []).map((d: any) => ({
        id: d.id,
        department_id: d.department_id,
        name: d.name,
        code: d.code,
        description: d.description || '',
        sub_departments: (d.sub_departments || []).map((s: any) => ({
          id: s.id,
          sub_department_id: s.sub_department_id,
          name: s.name,
          keywords: s.keywords || [],
          default_priority: s.default_priority || 'normal',
          description: s.description || '',
          is_default: s.is_default || false,
        })),
      })),
      intents: (existingIntents || []).map((intent: any) => ({
        id: intent.id,
        intent_id: intent.intent_id,
        name: intent.name,
        description: intent.description || '',
        examples: intent.examples || [],
        indicators: intent.indicators || [],
        workflow: intent.workflow,
        priority: intent.priority || 1,
        is_default: intent.is_default || false,
        require_address_verification: intent.require_address_verification || false,
      })),
      app_connection_id: existingConfig?.app_connection ?? null,
      serviceArea: (() => {
        if (!existingSA) return INITIAL_WIZARD_DATA.serviceArea
        const hasData = existingSA.city_name || existingSA.center_lat || existingSA.center_lng
        return {
          enabled: !!hasData,
          city_name: existingSA.city_name || '',
          description: existingSA.description || '',
          allowed_zip_codes: (existingSA.allowed_zip_codes || []).join(', '),
          center_lat: existingSA.center_lat || 0,
          center_lng: existingSA.center_lng || 0,
          default_zoom: existingSA.default_zoom || 12,
          bounds_north: existingSA.bounds_north || 0,
          bounds_south: existingSA.bounds_south || 0,
          bounds_east: existingSA.bounds_east || 0,
          bounds_west: existingSA.bounds_west || 0,
          skip_address_validation: existingConfig.skip_address_validation ?? false,
        }
      })(),
    }
    setWizardData(populated)
    setLoaded(true)
  }, [isEdit, existingConfig, existingIntents, existingDepts, existingSA])

  const validateStep = useCallback(
    (step: number): boolean => {
      const newErrors: Record<string, string> = {}
      if (step === 1 && !wizardData.configName.trim()) {
        newErrors.configName = 'Configuration name is required'
      }
      setErrors(newErrors)
      return Object.keys(newErrors).length === 0
    },
    [wizardData]
  )

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, WIZARD_STEPS.length))
    }
  }

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1))
  }

  const handleStepClick = (stepId: number) => {
    if (currentStep === 1 && stepId > 1 && !validateStep(1)) return
    setCurrentStep(stepId)
  }

  const handleCancel = () => {
    const hasData = wizardData.configName.trim() || wizardData.departments.length > 0 || wizardData.intents.length > 0
    if (hasData && !isEdit) {
      setShowCancelDialog(true)
    } else {
      navigate('/app/sro')
    }
  }

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return
    setIsSubmitting(true)

    try {
      let targetConfigId = numConfigId

      // Step A: Create or update the config itself
      const configPayload = {
        name: wizardData.configName.trim(),
        llm_provider: wizardData.provider,
        llm_model: wizardData.model,
        session_ttl: wizardData.sessionTTL,
        is_enabled: wizardData.enableSRO,
        skip_address_validation: wizardData.serviceArea.skip_address_validation,
      }

      if (isEdit && targetConfigId) {
        await updateConfig.mutateAsync({ configId: targetConfigId, data: configPayload })
      } else {
        const res = await createConfig.mutateAsync({ name: configPayload.name })
        targetConfigId = res.data.id as number
        if (!targetConfigId) throw new Error('Failed to resolve config ID')
        await updateConfig.mutateAsync({ configId: targetConfigId, data: configPayload })
      }

      // Step B: Sync departments
      await syncDepartments(targetConfigId, wizardData.departments, existingDepts || [])

      // Step C: Sync intents
      await syncIntents(targetConfigId, wizardData.intents, existingIntents || [])

      // Step D: Service area
      if (wizardData.serviceArea.enabled) {
        await updateSA.mutateAsync({
          configId: targetConfigId,
          data: {
            city_name: wizardData.serviceArea.city_name,
            description: wizardData.serviceArea.description,
            allowed_zip_codes: wizardData.serviceArea.allowed_zip_codes
              .split(',')
              .map((z) => z.trim())
              .filter(Boolean),
            center_lat: wizardData.serviceArea.center_lat,
            center_lng: wizardData.serviceArea.center_lng,
            default_zoom: wizardData.serviceArea.default_zoom,
            bounds_north: wizardData.serviceArea.bounds_north,
            bounds_south: wizardData.serviceArea.bounds_south,
            bounds_east: wizardData.serviceArea.bounds_east,
            bounds_west: wizardData.serviceArea.bounds_west,
          },
        })
      }

      // Step E: Update app_connection on SRO config
      // The app_connection_id is sent as part of the config update (Step A),
      // but if it changed, re-patch it now
      if (wizardData.app_connection_id !== existingConfig?.app_connection) {
        await updateConfig.mutateAsync({
          configId: targetConfigId,
          data: { app_connection: wizardData.app_connection_id },
        })
      }

      toast({
        title: isEdit ? 'Configuration updated' : 'Configuration created',
        description: `"${wizardData.configName}" has been saved.`,
      })

      navigate('/app/sro')
    } catch (error: any) {
      const msg = error?.response?.data?.error || error?.message || 'Failed to save configuration'
      toast({ title: 'Error', description: msg, variant: 'destructive' })
    } finally {
      setIsSubmitting(false)
    }
  }

  // --- Sync helpers ---

  async function syncDepartments(
    cfgId: number,
    desired: WizardDepartment[],
    existing: any[]
  ) {
    const existingIds = new Set(existing.map((d: any) => d.id))
    const desiredIds = new Set(desired.filter((d) => d.id).map((d) => d.id))

    // Delete removed departments
    for (const ex of existing) {
      if (!desiredIds.has(ex.id)) {
        await deleteDept.mutateAsync({ configId: cfgId, id: ex.id })
      }
    }

    // Create or update departments
    for (const dept of desired) {
      const payload = {
        department_id: dept.department_id,
        name: dept.name,
        code: dept.code,
        description: dept.description,
      }

      let deptId: number
      if (dept.id && existingIds.has(dept.id)) {
        await updateDept.mutateAsync({ configId: cfgId, id: dept.id, data: payload })
        deptId = dept.id
      } else {
        const res = await createDept.mutateAsync({ configId: cfgId, data: payload })
        deptId = res.data.id
      }

      // Sync sub-departments
      const existingDept = existing.find((d: any) => d.id === dept.id)
      const existingSubs: any[] = existingDept?.sub_departments || []
      const existingSubIds = new Set(existingSubs.map((s: any) => s.id))
      const desiredSubIds = new Set(dept.sub_departments.filter((s) => s.id).map((s) => s.id))

      // Delete removed subs
      for (const es of existingSubs) {
        if (!desiredSubIds.has(es.id)) {
          await deleteSub.mutateAsync({ configId: cfgId, deptId, subId: es.id })
        }
      }

      // Create or update subs
      for (const sub of dept.sub_departments) {
        const subPayload = {
          sub_department_id: sub.sub_department_id,
          name: sub.name,
          keywords: sub.keywords,
          default_priority: sub.default_priority,
          description: sub.description,
          is_default: sub.is_default,
        }
        if (sub.id && existingSubIds.has(sub.id)) {
          await updateSub.mutateAsync({ configId: cfgId, deptId, subId: sub.id, data: subPayload })
        } else {
          await createSub.mutateAsync({ configId: cfgId, deptId, data: subPayload })
        }
      }
    }
  }

  async function syncIntents(cfgId: number, desired: WizardIntent[], existing: any[]) {
    const existingIds = new Set(existing.map((i: any) => i.id))
    const desiredIds = new Set(desired.filter((i) => i.id).map((i) => i.id))

    // Delete removed intents
    for (const ex of existing) {
      if (!desiredIds.has(ex.id)) {
        await deleteIntent.mutateAsync({ configId: cfgId, id: ex.id })
      }
    }

    // Create or update intents
    for (const intent of desired) {
      const payload = {
        intent_id: intent.intent_id,
        name: intent.name,
        description: intent.description,
        examples: intent.examples,
        indicators: intent.indicators,
        workflow: intent.workflow,
        priority: intent.priority,
        is_default: intent.is_default,
        require_address_verification: intent.require_address_verification,
      }
      if (intent.id && existingIds.has(intent.id)) {
        await updateIntent.mutateAsync({ configId: cfgId, id: intent.id, data: payload })
      } else {
        await createIntent.mutateAsync({ configId: cfgId, data: payload })
      }
    }
  }

  // --- Render ---

  const stepDef = WIZARD_STEPS[currentStep - 1]
  const isFirst = currentStep === 1
  const isLast = currentStep === WIZARD_STEPS.length

  if (isEdit && (configLoading || !loaded)) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <StepBasics data={wizardData} onChange={setWizardData} errors={errors} />
      case 2:
        return <StepDepartments data={wizardData} onChange={setWizardData} />
      case 3:
        return <StepIntents data={wizardData} onChange={setWizardData} />
      case 4:
        return <StepIntegration data={wizardData} onChange={setWizardData} />
      case 5:
        return <StepServiceArea data={wizardData} onChange={setWizardData} />
      case 6:
        return <StepReview data={wizardData} />
      default:
        return null
    }
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] -m-6">
      {/* Sidebar Stepper */}
      <div className="w-64 min-w-[16rem] border-r bg-muted/30 flex flex-col">
        <div className="px-5 pt-6 pb-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            SRO Setup
          </p>
          <p className="text-sm font-bold mt-1 truncate">
            {wizardData.configName || 'New Configuration'}
          </p>
        </div>

        <nav className="flex-1 flex flex-col gap-0.5 px-2">
          {WIZARD_STEPS.map((step) => {
            const isActive = step.id === currentStep
            const isPast = step.id < currentStep
            return (
              <button
                key={step.id}
                type="button"
                onClick={() => handleStepClick(step.id)}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-md text-left transition-colors w-full',
                  isActive && 'bg-primary/10 border-l-2 border-primary',
                  !isActive && 'hover:bg-muted/50'
                )}
              >
                <div
                  className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-colors',
                    isActive && 'bg-primary text-primary-foreground',
                    isPast && 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
                    !isActive && !isPast && 'bg-muted text-muted-foreground'
                  )}
                >
                  {isPast ? <Check className="h-4 w-4" /> : step.id}
                </div>
                <div className="min-w-0">
                  <p
                    className={cn(
                      'text-sm font-medium truncate',
                      isActive && 'text-primary',
                      isPast && 'text-foreground',
                      !isActive && !isPast && 'text-muted-foreground'
                    )}
                  >
                    {step.label}
                  </p>
                  <p className="text-[11px] text-muted-foreground truncate">{step.description}</p>
                </div>
              </button>
            )
          })}
        </nav>

        {/* Progress bar */}
        <div className="px-5 pb-5 mt-auto">
          <p className="text-xs text-muted-foreground mb-2">
            Step {currentStep} of {WIZARD_STEPS.length}
          </p>
          <div className="h-1 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all duration-300"
              style={{ width: `${(currentStep / WIZARD_STEPS.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="border-b px-8 py-4 flex items-center justify-between shrink-0">
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Step {currentStep}
            </p>
            <h2 className="text-xl font-bold">{stepDef.label}</h2>
          </div>
          <span
            className={cn(
              'text-xs font-semibold px-2.5 py-1 rounded-full',
              wizardData.enableSRO
                ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                : 'bg-muted text-muted-foreground'
            )}
          >
            {wizardData.enableSRO ? 'SRO Enabled' : 'SRO Disabled'}
          </span>
        </div>

        {/* Step content (scrollable) */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          <div className="max-w-2xl">{renderStep()}</div>
        </div>

        {/* Footer nav */}
        <div className="border-t px-8 py-4 flex items-center justify-between shrink-0">
          <Button variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>

          <div className="flex items-center gap-3">
            {!isLast && currentStep > 1 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentStep(WIZARD_STEPS.length)}
              >
                Skip to Review
              </Button>
            )}

            {!isFirst && (
              <Button variant="outline" onClick={handleBack}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            )}

            {isLast ? (
              <Button onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Configuration
                  </>
                )}
              </Button>
            ) : (
              <Button onClick={handleNext}>
                Continue
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave? Your progress will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No, continue editing</AlertDialogCancel>
            <AlertDialogAction onClick={() => navigate('/app/sro')}>
              Yes, discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
